import { Camera, UserRound, ArrowRight, ShieldCheck } from 'lucide-react'

// Animated notice shown before the FIRST verification session: it makes it unmistakable
// that the live "match selfie" the candidate is about to take is placed on their official
// TruCheck™ Digital Card (a generic photo-ID layout — portrait on the left, details on the
// right) and used by employers to confirm their identity. Pure CSS animation (no deps),
// disabled under prefers-reduced-motion. The mock intentionally shows NO real card data.
const STYLES = `
@keyframes tcScan { 0% { top: -20%; opacity: 0 } 15% { opacity: 1 } 85% { opacity: 1 } 100% { top: 100%; opacity: 0 } }
@keyframes tcFlash { 0%, 66%, 100% { opacity: 0 } 6%, 11% { opacity: .75 } }
@keyframes tcSheen { 0% { transform: translateX(-160%) skewX(-16deg) } 55%, 100% { transform: translateX(560%) skewX(-16deg) } }
@keyframes tcLine { 0%, 100% { opacity: .5 } 50% { opacity: 1 } }
@keyframes tcDot { 0%, 100% { opacity: 1; transform: scale(1) } 50% { opacity: .35; transform: scale(.8) } }
@keyframes tcAvatar { 0%, 100% { transform: scale(1) } 50% { transform: scale(1.05) } }
@keyframes tcArrow { 0%, 100% { transform: translateX(0); opacity: .5 } 50% { transform: translateX(4px); opacity: 1 } }
@media (prefers-reduced-motion: reduce) { .tcAnim { animation: none !important } }
`

const TruCheckCardPhotoNotice = () => {
    return (
        <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-5 sm:p-6">
            <style>{STYLES}</style>

            <div className="flex items-start gap-3">
                <div className="w-11 h-11 shrink-0 rounded-full bg-blue-100 border border-blue-300 flex items-center justify-center">
                    <Camera className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                    <h3 className="text-base font-bold text-blue-800">
                        Your live selfie becomes your TruCheck™ Card photo
                    </h3>
                    <p className="text-sm text-blue-900/80 mt-0.5">
                        The <strong>“Match Selfie”</strong> you take in this step is placed on your official
                        TruCheck™ Digital Card and shown to potential employers/interviewers to confirm your
                        identity. Please be in <strong>professional / business attire</strong>.
                    </p>
                </div>
            </div>

            {/* Selfie -> Card animation */}
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                {/* Live selfie bubble */}
                <div className="relative shrink-0">
                    <div className="tcAnim w-16 h-16 sm:w-[68px] sm:h-[68px] rounded-full bg-gradient-to-b from-blue-100 to-indigo-100 border border-blue-300 flex items-center justify-center overflow-hidden"
                        style={{ animation: 'tcAvatar 3.2s ease-in-out infinite' }}>
                        <UserRound className="w-8 h-8 text-blue-500" />
                        <div className="tcAnim absolute inset-0 bg-white" style={{ animation: 'tcFlash 3.2s ease-in-out infinite' }} />
                    </div>
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white border border-blue-200 shadow-sm">
                        <span className="tcAnim w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" style={{ animation: 'tcDot 1.4s ease-in-out infinite' }} />
                        <span className="text-[8px] font-bold text-gray-600 tracking-wide">LIVE</span>
                    </span>
                </div>

                <ArrowRight className="tcAnim w-5 h-5 text-blue-400 shrink-0" style={{ animation: 'tcArrow 1.3s ease-in-out infinite' }} />

                {/* TruCheck card mock (generic photo-ID layout) */}
                <div className="relative overflow-hidden rounded-xl bg-white shadow-lg border border-gray-200 w-full max-w-[280px]">
                    {/* Header bar */}
                    <div className="h-7 px-3 bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center">
                        <span className="text-white text-[10px] font-extrabold tracking-wide">TruCheck™</span>
                        <span className="ml-auto flex items-center gap-1">
                            <span className="tcAnim w-1.5 h-1.5 rounded-full bg-emerald-300 inline-block" style={{ animation: 'tcDot 1.4s ease-in-out infinite' }} />
                            <span className="text-white/90 text-[8px] font-bold tracking-wider">VERIFIED</span>
                        </span>
                    </div>

                    {/* Body: photo (left) + details (right) */}
                    <div className="flex gap-3 p-3">
                        <div className="relative shrink-0 w-[68px] h-[82px] rounded-lg overflow-hidden bg-gradient-to-b from-blue-100 to-indigo-100 border border-blue-200 flex items-center justify-center">
                            <UserRound className="w-9 h-9 text-blue-500" />
                            {/* live scan line */}
                            <div className="tcAnim absolute left-0 right-0 h-4 bg-gradient-to-b from-transparent via-blue-400/40 to-transparent"
                                style={{ animation: 'tcScan 3.2s ease-in-out infinite' }} />
                            {/* capture flash */}
                            <div className="tcAnim absolute inset-0 bg-white" style={{ animation: 'tcFlash 3.2s ease-in-out infinite' }} />
                        </div>

                        <div className="flex-1 min-w-0 space-y-2 pt-1">
                            <div className="tcAnim h-2.5 rounded bg-gray-300 w-4/5" style={{ animation: 'tcLine 2.4s ease-in-out infinite' }} />
                            <div className="tcAnim h-2 rounded bg-gray-200 w-3/5" style={{ animation: 'tcLine 2.4s ease-in-out infinite .3s' }} />
                            <div className="tcAnim h-2 rounded bg-gray-200 w-2/3" style={{ animation: 'tcLine 2.4s ease-in-out infinite .6s' }} />
                            <div className="tcAnim h-2 rounded bg-gray-200 w-1/2" style={{ animation: 'tcLine 2.4s ease-in-out infinite .9s' }} />
                            <div className="flex items-center gap-1.5 pt-1">
                                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                <div className="h-2 rounded bg-emerald-200 w-16" />
                            </div>
                        </div>
                    </div>

                    {/* Diagonal sheen sweep */}
                    <div className="tcAnim pointer-events-none absolute top-0 left-0 h-full w-10 bg-gradient-to-r from-transparent via-white/70 to-transparent"
                        style={{ animation: 'tcSheen 4.5s ease-in-out infinite' }} />
                </div>
            </div>

            <p className="mt-4 text-center text-xs text-blue-900/70">
                Illustration only — no real card details are shown. Your photo is added after you clear this check.
            </p>
        </div>
    )
}

export default TruCheckCardPhotoNotice
